import { motion } from 'motion/react';
import { useInView } from '../../../components/hooks/useInView.jsx';

export function ProblemStatement() {
  const [ref, isInView] = useInView({ threshold: 0.2 });

  const painPoints = [
    {
      title: 'Inconsistent Motivation',
      description: 'Lack of sustained motivation due to insufficient encouragement or tracking of progress.'
    },
    {
      title: 'Fragmented Solutions',
      description: 'Need for multiple apps to address workouts, meal planning, and tracking, creating a disjointed experience.'
    },
    {
      title: 'Lack of Personalized Insights',
      description: 'Lack of meaningful insights from tracked data, leaving users unsure about their progress.'
    }
  ];

  return (
    <section ref={ref} className="min-h-screen flex items-center py-20 px-6 bg-black/30">
      <div className="max-w-7xl mx-auto w-full">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-5xl md:text-6xl mb-8">
            The Problem
          </h2>
          <div className="h-1 w-20 bg-[#fd5b38] mx-auto mb-8" />
          <p className="text-xl md:text-2xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
            Many individuals struggle to maintain a healthy lifestyle due to a lack of personalized guidance on 
            fitness routines, meal planning, and activity tracking. Existing solutions often focus on one aspect, 
            such as workouts or diet, but fail to integrate all key areas into a single, cohesive experience.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8 mb-16">
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="bg-gradient-to-br from-[#fd5b38]/20 to-transparent p-8 rounded-2xl item-border-1 border-[#fd5b38]/30"
          >
            <div className="text-6xl md:text-7xl text-[#fd5b38] mb-4">70%</div>
            <p className="text-xl text-gray-300">
              of fitness app users churn within 90 days due to lack of engagement
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="bg-gradient-to-br from-[#fd5b38]/20 to-transparent p-8 rounded-2xl item-border-1 border-[#fd5b38]/30"
          >
            <div className="text-6xl md:text-7xl text-[#fd5b38] mb-4">55%</div>
            <p className="text-xl text-gray-300">
              of individuals who start fitness journeys fail due to insufficient guidance
            </p>
          </motion.div>
        </div>

        <motion.h3
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.7 }}
          className="text-4xl md:text-5xl mb-12 text-center"
        >
          Key Pain Points
        </motion.h3>

        <div className="grid md:grid-cols-3 gap-8">
          {painPoints.map((point, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.5 }}
              animate={isInView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.6, delay: 0.8 + index * 0.2 }}
              whileHover={{ scale: 1.05 }}
              className="bg-[#1a1a1a] p-8 rounded-2xl item-border-1 border-gray-800 hover:border-[#fd5b38] transition-all duration-300"
            >
              <div className="w-12 h-12 bg-[#fd5b38]/20 rounded-full flex items-center justify-center mb-6">
                <div className="w-6 h-6 bg-[#fd5b38] rounded-full" />
              </div>
              <h4 className="text-2xl mb-4 text-[#fd5b38]">{point.title}</h4>
              <p className="text-gray-400">{point.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
